/**
 * Einmal neu laden, wenn ein Teil der Oberfläche fehlt — nie in Schleife.
 *
 * Nach einem Update liegen auf dem Server neue Bündel. Ein Reiter, der noch die
 * alte Oberfläche zeigt, fordert beim Öffnen einer Seite ein Bündel an, das es
 * nicht mehr gibt; der Ladefehler stand nur in der Konsole, die Seite blieb
 * leer (Testgrundlage 2.2, B-01 und T00: „Einstellungen“). Ein Neuladen holt
 * die aktuelle Oberfläche und mit ihr die passenden Bündel.
 *
 * Scheitert es danach gleich wieder, liegt es nicht an einer alten Fassung —
 * dann bleibt die Seite stehen und zeigt den Hinweis mit „Neu laden“, statt
 * sich ohne Ende neu zu laden. Gemerkt wird das im sessionStorage: es gilt je
 * Reiter und überlebt das Neuladen.
 */
const SCHLUESSEL = 'revilion:neu-geladen';

/** Innerhalb dieser Frist nach einem automatischen Neuladen kein zweites. */
export const RELOAD_LOCK_MS = 60_000;

let neuLadenLaeuft = false;

/** Die Seite vollständig neu laden — eigene Funktion, damit Tests sie ersetzen können. */
export function reloadPage(): void {
  window.location.reload();
}

/** Läuft gerade ein automatisches Neuladen? Dann nichts anzeigen, nur warten. */
export function isReloadPending(): boolean {
  return neuLadenLaeuft;
}

/**
 * Lädt die Seite neu, wenn das in der letzten Minute nicht schon geschehen
 * ist. `true`, wenn neu geladen wird.
 */
export function reloadOnce(jetzt: number = Date.now()): boolean {
  if (neuLadenLaeuft) return true;
  try {
    const zuletzt = Number(sessionStorage.getItem(SCHLUESSEL) ?? 0);
    if (zuletzt && jetzt - zuletzt < RELOAD_LOCK_MS) return false;
    sessionStorage.setItem(SCHLUESSEL, String(jetzt));
  } catch {
    // Ohne sessionStorage (gesperrt, privates Fenster) kein Schutz gegen die
    // Schleife — dann lieber nicht automatisch neu laden.
    return false;
  }
  neuLadenLaeuft = true;
  reloadPage();
  return true;
}

/**
 * Ist das ein Fehler beim Nachladen eines Bündels? Die Browser formulieren ihn
 * verschieden; Vite meldet fehlende Stylesheets eigens.
 */
export function isChunkLoadError(fehler: unknown): boolean {
  const text = `${(fehler as { name?: string })?.name ?? ''} ${(fehler as { message?: string })?.message ?? fehler ?? ''}`;
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError|Expected a JavaScript module script/i.test(
    text,
  );
}

/** Nur für Tests: den Zustand dieses Moduls zurücksetzen. */
export function resetReloadStateForTests(): void {
  neuLadenLaeuft = false;
}
